import axios from '@fs/zion-axios'
import { clearStartingPidCache } from '@fs/zion-tree-groups'
import { appendQueryParams } from '../helpers/url_helper'
import { getGroupsList } from './groupservices'
import relationshipOptions from '../pages/Avatar/FamilyAvatar/relationshipsData'

export const cdnRoot = 'https://common-discovery-service-pgp.familysearchcdn.org'
const serviceBase = '/service/discovery'
const orchestrationBase = `${serviceBase}/mystory/orchestration`
const profileServiceBase = `${orchestrationBase}/profile`

export const getMyPoints = () => axios.get(`${orchestrationBase}/leaderboard/my-points`)

export const doMigration = () => {
  return axios.post(`${orchestrationBase}/data-migration`)
}

/*
 * MEMORIES
 */

export const getPhotosForAlbum = (activityGroupId) => {
  if (!activityGroupId) return undefined
  return activityGroupId && axios.get(`${orchestrationBase}/activity-groups/${activityGroupId}/photo-albums`)
}
export const savePhotoForAlbum = (data, activityGroupId) => {
  if (!data || !activityGroupId) return undefined

  return activityGroupId && axios.post(`${orchestrationBase}/activity-groups/${activityGroupId}/photo-albums`, data)
}
export const deletePhotoFromAlbum = ({ activityGroupId, id, deleteFromMemories = false }) =>
  activityGroupId &&
  id &&
  axios.delete(
    `${orchestrationBase}/activity-groups/${activityGroupId}/photo-albums/entry/${id}?includeMemoryGalleryPhotos=${deleteFromMemories}`
  )

/**
 * Retrieves a memory by its ID.
 *
 * @param {Object} params - The parameters for retrieving the memory.
 * @param {string} params.memoryArtifactId - The ID of the memory artifact.
 * @returns {Promise} A promise that resolves to the memory data.
 */
export const getMemoryById = ({ memoryArtifactId }) => {
  if (!memoryArtifactId) return undefined
  return axios.get(`${orchestrationBase}/memories/${memoryArtifactId}`)
}

/*
 * TEMPLE
 */

export const getCurrentTemple = () => axios.get(`${serviceBase}/common/temple/assigned`)

export const getTempleEligible = () => axios.get(`${orchestrationBase}/temple/eligible`)

export const saveTempleCountdownDismiss = () => axios.post(`${orchestrationBase}/temple/countdown/dismiss`)

export const getTempleCountdownDismiss = () => axios.get(`${orchestrationBase}/temple/countdown/dismiss`)

/*
 * ACTIVITY GROUPS & ANSWERS
 */

export const postActivityGroupDismiss = ({ activityGroupId }) => {
  if (!activityGroupId) return undefined
  return axios.post(`/service/discovery/mystory/orchestration/activity-groups/${activityGroupId}/dismiss`)
}

export const deleteActivityGroupDismiss = ({ activityGroupId }) => {
  if (!activityGroupId) return undefined
  return axios.delete(`/service/discovery/mystory/orchestration/activity-groups/${activityGroupId}/dismiss`)
}

export const markAsComplete = ({ activityGroupId, activityGroupAnswerId }) => {
  if (!activityGroupId || !activityGroupAnswerId) return undefined
  return axios.put(`${orchestrationBase}/activity-groups/${activityGroupId}/answers/${activityGroupAnswerId}/complete`)
}

export const getActivities = (activityGroupId) => {
  if (!activityGroupId) return undefined
  return axios.get(`${orchestrationBase}/activity-groups/${activityGroupId}/activities`)
}

export const getActivityGroupIcons = () => {
  return axios.get(`${orchestrationBase}/activity-groups/icons`)
}

export const getFilters = () => {
  return axios.get(`${orchestrationBase}/filters`)
}

export const getActivityGroupAnswerCount = () => {
  return axios.get(`${orchestrationBase}/answers/completed/summary`)
}

export const getActivityGroupAnswers = ({ activityGroupId, forPersonId }) => {
  if (!activityGroupId) return undefined
  const url = appendQueryParams(`${orchestrationBase}/activity-groups/${activityGroupId}/answers`, {
    answerForPersonId: forPersonId,
  })
  return axios.get(url)
}

// Retrieve a specific activityGroupAnswer by its ID and owner's cisID.
export const getActivityGroupAnswerById = ({ queryKey }) => {
  const { activityGroupId, activityGroupAnswerId, cisUserId } = queryKey?.[1] ?? {} // use updated ReactQuery params
  if (!activityGroupId || !activityGroupAnswerId) return null // return if no activityGroupId or activityGroupAnswerId
  const url = appendQueryParams(
    `${orchestrationBase}/activity-groups/${activityGroupId}/answers/${activityGroupAnswerId}`,
    { ownerCisUserId: cisUserId }
  )
  return axios.get(url)
}

export const deleteAnswerGroup = ({ activityGroupId, activityGroupAnswerId }) => {
  if (!activityGroupId || !activityGroupAnswerId) return undefined
  return axios.delete(`${orchestrationBase}/activity-groups/${activityGroupId}/answers/${activityGroupAnswerId}`)
}

export const getStreaks = () => axios.get(`${orchestrationBase}/activity-groups/answers/streak`)

export const setGrouping = ({ activityGroupId, activityGroupAnswerId, grouping }) => {
  if (!activityGroupId || !activityGroupAnswerId || !grouping) return undefined
  return axios({
    method: 'PUT',
    url: `${orchestrationBase}/activity-groups/${activityGroupId}/answers/${activityGroupAnswerId}/grouping`,
    data: { grouping },
  })
}

export const getMemoryFromMemories = ({ artifactId }) => {
  if (!artifactId) return undefined
  return axios.get(
    `/service/memories/manager/artifacts/${artifactId}?includeAssociatedArtifacts=true&includeDatesPlaces=true&includeTags=true`
  )
}

export const saveMemoryDescription = ({ artifactId, description }) => {
  if (!artifactId || !description) return undefined
  return axios({
    method: 'PUT',
    url: `/service/memories/manager/artifacts/${artifactId}/files`,
    data: description,
    headers: { 'Content-Type': 'text/plain' },
  })
}

export const saveMemoryTitle = ({ artifactId, artifact }) => {
  if (!artifactId || !artifact) return undefined
  return axios({
    method: 'POST',
    url: `/service/memories/manager/artifacts/${artifactId}`,
    data: artifact,
    headers: { 'Content-Type': 'application/json' },
  })
}

export const updateMemoryDate = ({ artifactId, ...rest }) => {
  return (
    artifactId &&
    (rest.dateNormalizedText || rest.dateNonStandardizedText) &&
    axios({
      method: 'PUT',
      url: `/service/memories/manager/artifacts/${artifactId}/datesplaces/DEFAULT/date`,
      data: {
        id: artifactId,
        ...rest,
      },
      headers: { 'Content-Type': 'application/json' },
    })
  )
}

export const addImagesToMemory = ({ artifactId, photoArtifactIds }) => {
  let requests = []
  if (artifactId && photoArtifactIds?.length) {
    requests = photoArtifactIds.map((photoArtifactId) =>
      axios({
        method: 'POST',
        url: `/service/memories/manager/artifacts/${artifactId}/artifacts?otherArtifactId=${photoArtifactId}&setAsIcon=true`,
      })
    )
  }
  return Promise.all(requests)
}

export const removeImageFromMemory = ({ artifactId, photoArtifactId }) => {
  if (!artifactId || !photoArtifactId) return undefined
  return axios.delete(`/service/memories/manager/artifacts/${artifactId}/artifacts?otherArtifactId=${photoArtifactId}`)
}

export const saveAnswers = ({ activityGroupId, activityGroupAnswerId, data }) => {
  if (!activityGroupId || !data) return undefined
  const saveUrl = `${orchestrationBase}/activity-groups/${activityGroupId}/answers`
  const updateUrl = `${saveUrl}/${activityGroupAnswerId}`
  const isCreate = !activityGroupAnswerId

  return axios({
    method: isCreate ? 'POST' : 'PUT',
    url: isCreate ? saveUrl : updateUrl,
    data,
  })
}

/*
 * FAMILY GROUPS
 */

export const getFamilyGroups = (includePendingGroups) => {
  return includePendingGroups ? getGroupsList() : axios.get(`${orchestrationBase}/family-groups`)
}

export const getFamilyGroupsWithPoints = () => axios.get(`${orchestrationBase}/leaderboard/family-groups/points`)

/*
 * INVITATIONS
 */

export const deleteFamilyGroupInvitesByInviteId = ({ invitationId }) => {
  if (!invitationId) return undefined
  return axios.delete(`${orchestrationBase}/invitations/${invitationId}`)
}

export const deleteFamilyGroupInvitesByActivityGroupId = ({ activityGroupId }) => {
  if (!activityGroupId) return undefined
  return axios.delete(`${orchestrationBase}/invitations/received?activityGroupId=${activityGroupId}`)
}

export const getInvitationsReceived = () => axios.get(`${orchestrationBase}/invitations/received`)

export const postActivityInvites = ({ activityNameOrId, data }) => {
  if (!activityNameOrId || !data) return undefined
  return axios.post(`${orchestrationBase}/activity-groups/${activityNameOrId}/invitations`, data)
}

export const getCampaigns = (isMember = true) =>
  axios.get('/service/campaign/sna/api/summary/userSummary', { params: { isMember } })

/*
 * FAMILY TREE PEOPLE
 */

export const getFamilyGenerations = (generationsUp = 4, generationsDown = 2) => {
  const params = { generationUp: generationsUp, generationDown: generationsDown } // default gets grandchildren through great-grandparents
  return axios.get(`/service/discovery/mystory/orchestration/person/family`, { params })
}

/*
 * LITE PEOPLE
 */

export const getAllPersonLites = (relationship) =>
  axios.get(`${orchestrationBase}/person/lite${relationship ? `?relationship=${relationship}` : ''}`)

export const getPersonLite = (personLiteId) => {
  if (!personLiteId) return undefined
  return axios.get(`${orchestrationBase}/person/lite/${personLiteId}`)
}

export const createPersonLite = ({ givenName, surname, gender, relationship }) => {
  const genders = ['MALE', 'FEMALE', 'UNKNOWN']
  const personDataProvided = givenName && surname && gender && relationship
  if (!personDataProvided) return undefined

  return axios.post(`${orchestrationBase}/person/lite`, {
    givenName,
    surname,
    gender: genders?.includes(gender) ? gender : null,
    relationship: relationshipOptions?.find((option) => option.relationship === relationship)?.relationship,
  })
}

export const updatePersonLite = ({ personLiteId, givenName, surname, gender, relationship }) => {
  const genders = ['MALE', 'FEMALE', 'UNKNOWN']
  if (!personLiteId) return undefined

  return axios.put(`${orchestrationBase}/person/lite/${personLiteId}`, {
    givenName,
    surname,
    gender: genders?.includes(gender) ? gender : null,
    relationship: relationshipOptions?.find((option) => option.relationship === relationship) ? relationship : null,
  })
}

export const deletePersonLite = (personLiteId) => {
  if (!personLiteId) return undefined
  return axios.delete(`${orchestrationBase}/person/lite/${personLiteId}`)
}

/*
 * AVATARS
 */

/**
 * @param {string} forPersonId treePersonId or litePersonId
 * @param {*} formData avatar specs data
 * @param {string} avatarId ID if updating an existing avatar
 */
export const saveAvatar = (forPersonId, formData, avatarId) => {
  if (!forPersonId) return undefined

  return (
    forPersonId &&
    axios({
      method: avatarId ? 'put' : 'post', // create or update avatar
      url: `${profileServiceBase}/${forPersonId}/avatars${avatarId ? `/${avatarId}` : ''}`,
      data: formData,
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  )
}

/**
 * - Sets an avatar as the active one for the tree/lite person it belongs to.
 *   - Does NOT allow unsetting. Unsetting is accomplished by deleting all avatars for a person
 * @param {string} forPersonId tree person (or lite person) id to set an active avatar for
 * @param {string} avatarId id of avatar to set as active
 */
export const setActiveAvatar = ({ forPersonId, avatarId }) => {
  if (!forPersonId || !avatarId) return undefined
  return axios.put(`${profileServiceBase}/${forPersonId}/avatars/${avatarId}/active`)
}

/**
 * - Front-end can filter these by personId when displaying ones for only one tree person
 * @returns {array} all avatars a user has created for themselves, tree people in their groups, & lite persons they've created
 */
export const getAllAvatars = (enabled) => {
  return enabled ? axios.get(`${profileServiceBase}/avatars`) : []
}

/**
 * @param {string} avatarId id of avatar to fetch
 * @returns {object} detailed specs data of an avatar (ie: hair, eyes, skin, ears, etc)
 */
export const getAvatarSpecs = (avatarId) => {
  if (!avatarId) return null // This one returns null to maintain existing behavior
  return axios.get(`${profileServiceBase}/avatars/${avatarId}/specs`)
}

/**
 * Deletes an avatar
 * @param {string} forPersonId id of tree/lite person avatar belongs to
 * @param {string} avatarId id of avatar to delete
 */
export const deleteAvatar = ({ forPersonId, avatarId }) => {
  if (!forPersonId || !avatarId) return undefined
  return axios.delete(`${profileServiceBase}/${forPersonId}/avatars/${avatarId}`)
}

/*
 * ACTIVE IMAGES (AVATAR|CUSTOM|PORTRAIT)
 * Deprecated as of JAN 2024, but could be used again if image-as-avatar feature returns
 */
/** @deprecated ActiveImg endpoints no longer in use, but feature could return in future version */
export const setActiveImage = ({ forPersonId, source, itemId }) =>
  axios.put(
    `${orchestrationBase}/profile/${forPersonId}/images/active?source=${source}${itemId ? `&itemId=${itemId}` : ''}`
  )

/**
 * deletes record of preference, DOES NOT delete the actual image
 * @deprecated ActiveImg endpoints no longer in use, but feature could return in future version
 */
export const deleteActiveImage = (forPersonId) => {
  if (!forPersonId) return undefined
  return axios.delete(`${orchestrationBase}/profile/${forPersonId}/images/active`)
}

/** @deprecated ActiveImg endpoints no longer in use, but feature could return in future version */
export const getAllProfileImages = (forPersonId) => {
  if (!forPersonId) return undefined
  return axios.get(`${orchestrationBase}/profile/${forPersonId}/images`)
}

/**
 * use this only for CUSTOM profile images, not AVATARS or TREE_IMAGES
 * @deprecated ActiveImg endpoints no longer in use, but feature could return in future version
 */
export const deleteCustomProfileImage = (artifactId) => {
  if (!artifactId) return undefined
  return axios.delete(`${orchestrationBase}/profiles/${artifactId}?deleteFromGallery=true`)
}

/*
 * PREVIOUS FAMILY-GROUP ANSWERS
 */

export const markAnswerAsRead = (activityGroupAnswerId) => {
  if (!activityGroupAnswerId) return undefined
  return axios.post(`${orchestrationBase}/activity-groups/answers/${activityGroupAnswerId}/read`)
}

export const getSharingPreferences = (activityGroupAnswerId) => {
  if (!activityGroupAnswerId) return undefined
  return axios.get(`${orchestrationBase}/activity-groups/answers/${activityGroupAnswerId}/sharings`)
}

export const deleteSharingPreference = ({ activityGroupAnswerId, familyGroupId }) => {
  if (!activityGroupAnswerId || !familyGroupId) return undefined
  return axios.delete(
    `${orchestrationBase}/activity-groups/answers/${activityGroupAnswerId}/sharings/family-groups/${familyGroupId}`
  )
}
export const getConsolidatedAnswers = () => axios.get(`${orchestrationBase}/family/answers`)

/*
 * PUSH NOTIFICATIONS
 */

export const postPushNotificationRegistration = ({ deviceRegistrationToken, appId }) => {
  const payload = { appId, deviceRegistrationToken }
  return axios.post(`/service/mobile/api/v1/push/registration`, payload)
}

export const getGroupMembers = (groupId) => {
  return axios.get(`/service/tree/tree-data/groups/${groupId}/members`)
}

/**
 * Accept an invite made to the current user.
 * @param {string} groupId - The group's ID.
 * @param {string} inviteId - The invite ID generated for the email invitation.
 * @throws 400 - Invalid group id
 * @throws 400 - User is already a member of the group
 * @throws 400 - Unable to join group due to group or user limitations
 * @throws 400 - Invalid inviteId
 * @throws 403 - User is banished from the group
 * @throws 404 - Group not found
 * @throws 404 - Group invite not found
 * @throws 410 - Group is tombstoned
 * @returns {Promise} axios.post promise
 */
export const acceptInvitation = (groupId, inviteId) => {
  clearStartingPidCache()
  return axios.post(`/service/tree/tree-data/groups/${groupId}/invites/${inviteId}`)
}

/**
 * Decline an invite made to the current user.
 * @param {string} groupId - The group's ID.
 * @param {string} inviteId - The invite ID generated for the email invitation.
 * @throws 400 - Invalid group id
 * @throws 400 - User is already a member of the group
 * @throws 400 - Unable to join group due to group or user limitations
 * @throws 400 - Invalid inviteId
 * @throws 403 - User is banished from the group
 * @throws 404 - Group not found
 * @throws 404 - Group invite not found
 * @throws 410 - Group is tombstoned
 * @returns {Promise} axios.post promise
 */
export const declineInvitation = (groupId, inviteId) => {
  clearStartingPidCache()
  return axios.post(`/service/tree/tree-data/groups/${groupId}/invites/${inviteId}?reject=true`)
}

export const createInvitation = (groupId) => {
  return axios.post(`/service/tree/tree-data/groups/${groupId}/invites`)
}

/**
 * Read group invite information for a specific invite.
 * @param {*} groupId - The group's ID.
 * @param {*} inviteId - The invite ID generated for the email invitation.
 * @throws 400 - Invalid group id.
 * @throws 400 - Invalid invite id.
 * @throws 404 - Group not found.
 * @throws 404 - Group invite not found.
 * @throws 410 - Group is tombstoned.
 * @returns {Promise} Promise of axios.get
 */
export const getInviteInformation = (groupId, inviteId) => {
  return axios.get(`/service/tree/tree-data/groups/${groupId}/invites/${inviteId}`).then((res) => res.data)
}

export const getActivityGroupImages = () => {
  return axios.get(`${orchestrationBase}/activity-groups/images`)
}
